/**
 * Dropdown de modelos disponiveis para o user logado (M8 / chat
 * direto). Mostra capabilities chips (web, multimodal, reasoning) e
 * marca o default. Em thread existente, troca dispara
 * ``useUpdateChatThreadModel``. Em modo "tela vazia" (sem threadId),
 * seta apenas estado local que o DirectChatPage passa adiante.
 */
import { Brain, Cpu, Globe, Image as ImageIcon } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "@/components/ui/select";
import { useAllowedModels } from "@/features/chat/hooks/useAllowedModels";

interface Props {
  value: string;
  onChange: (id: string) => void;
  disabled?: boolean;
}

export function ModelSelector({ value, onChange, disabled }: Props) {
  const { t } = useTranslation("directChat");
  const models = useAllowedModels();
  const list = models.data ?? [];

  // Resolve o label compacto do modelo selecionado para o trigger
  // (em vez de deixar o ``SelectValue`` herdar o JSX cheio do item, que
  // quebra em multiplas linhas com os badges/icones).
  const selected = list.find((m) => m.id === value);
  const triggerLabel = selected?.display_name ?? "";

  return (
    <Select value={value} onValueChange={onChange} disabled={disabled}>
      <SelectTrigger className="h-9 min-w-[180px] max-w-[240px] gap-2 text-xs">
        <Cpu className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        <span className="min-w-0 flex-1 truncate text-left">
          {triggerLabel || t("modelSelector.placeholder")}
        </span>
      </SelectTrigger>
      <SelectContent>
        {list.length === 0 ? (
          <div className="px-2 py-1.5 text-xs text-muted-foreground">
            {t("noModelsAvailable")}
          </div>
        ) : null}
        {list.map((m) => (
          <SelectItem key={m.id} value={m.id}>
            <div className="flex items-center gap-2">
              <span className="font-medium">{m.display_name}</span>
              <Badge variant="outline" className="text-[10px] uppercase">
                {m.provider}
              </Badge>
              {m.is_default ? (
                <Badge variant="secondary" className="text-[10px]">
                  {t("modelSelector.default")}
                </Badge>
              ) : null}
              <span className="flex items-center gap-1">
                {m.supports_web_search ? (
                  <span
                    title={t("modelSelector.capabilities.webSearch")}
                    className="text-muted-foreground"
                  >
                    <Globe className="h-3 w-3" />
                  </span>
                ) : null}
                {m.supports_multimodal ? (
                  <span
                    title={t("modelSelector.capabilities.multimodal")}
                    className="text-muted-foreground"
                  >
                    <ImageIcon className="h-3 w-3" />
                  </span>
                ) : null}
                {m.supports_reasoning ? (
                  <span
                    title={t("modelSelector.capabilities.reasoning")}
                    className="text-muted-foreground"
                  >
                    <Brain className="h-3 w-3" />
                  </span>
                ) : null}
              </span>
            </div>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
